import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import {
  Brain, Search, Sparkles, ClipboardList, LayoutDashboard, UploadCloud,
  FileText, Trash2, ChevronRight, Copy, CheckCircle2, Loader2, X, Send, PenLine,
  Eye, Download,
} from "lucide-react";
import { adminApi } from "@/lib/api";

/**
 * Ms. Diane's Second Brain — an admin sub-tab.
 *
 * 4 modes:
 *   navigator   → cross-pillar dashboard + recent + open actions
 *   detective   → RAG chat over uploaded docs, cited answers
 *   drafter     → template-driven + freeform Claude drafting
 *   synthesizer → paste notes → summary + decisions + action items
 * plus a persistent left rail of the 5 Pillars with counts + upload.
 */
const MODES = [
  { id: "navigator",   label: "Navigator",       Icon: LayoutDashboard },
  { id: "detective",   label: "Doc Detective",   Icon: Search },
  { id: "drafter",     label: "Drafter",         Icon: PenLine },
  { id: "synthesizer", label: "Synthesizer",     Icon: ClipboardList },
];

export default function SecondBrain({ token }) {
  const api = useMemo(() => adminApi(token), [token]);
  const [pillars, setPillars] = useState([]);
  const [overview, setOverview] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [activePillar, setActivePillar] = useState("all");
  const [mode, setMode] = useState("navigator");
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState(null);   // { loading, doc, error }

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [p, o, d] = await Promise.all([
        api.get("/admin/second-brain/pillars"),
        api.get("/admin/second-brain/overview"),
        api.get("/admin/second-brain/documents"),
      ]);
      setPillars(p.data?.pillars || []);
      setOverview(o.data || null);
      setDocuments(d.data?.documents || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load Second Brain.");
    } finally { setLoading(false); }
  }, [api]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const uploadFile = async (pillar, file, tags) => {
    const fd = new FormData();
    fd.append("pillar", pillar);
    fd.append("tags", tags || "");
    fd.append("file", file);
    try {
      const r = await api.post("/admin/second-brain/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Ingested ${r.data.filename}`);
      await loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed.");
    }
  };

  const removeDoc = async (id) => {
    if (!window.confirm("Delete this document from the vault?")) return;
    try {
      await api.delete(`/admin/second-brain/documents/${id}`);
      toast.success("Removed.");
      await loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed.");
    }
  };

  const closeAction = async (id) => {
    try {
      await api.patch(`/admin/second-brain/actions/${id}`, { status: "done" });
      await loadAll();
    } catch { /* ignore */ }
  };

  const openPreview = async (id) => {
    setPreview({ loading: true, doc: null, error: "" });
    try {
      const r = await api.get(`/admin/second-brain/documents/${id}`);
      setPreview({ loading: false, doc: r.data, error: "" });
    } catch (e) {
      setPreview({ loading: false, doc: null, error: e?.response?.data?.detail || "Failed to load document." });
    }
  };

  const scopedDocs = useMemo(() => (
    activePillar === "all" ? documents : documents.filter((d) => d.pillar === activePillar)
  ), [activePillar, documents]);

  return (
    <div data-testid="admin-second-brain" className="grid grid-cols-1 lg:grid-cols-[280px_minmax(0,1fr)] gap-6">
      {/* LEFT RAIL — Pillars */}
      <aside className="space-y-4">
        <div className="bg-white border border-gray-100 rounded-2xl p-5">
          <div className="flex items-center gap-2 text-black">
            <span className="w-8 h-8 grid place-items-center rounded-lg bg-black text-white"><Brain size={16} /></span>
            <div>
              <p className="font-heading font-bold text-lg leading-none">Second Brain</p>
              <p className="text-[10px] uppercase tracking-widest text-gray-500 mt-1">Ms. Diane · Ecosystem</p>
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-3 leading-relaxed">
            Every SOP, deal doc, script, and note across the DW ecosystem — searchable, draft-ready, action-tracked.
          </p>
        </div>

        <PillarRail
          pillars={pillars}
          overview={overview}
          activePillar={activePillar}
          onSelect={setActivePillar}
          onUpload={uploadFile}
          totalDocs={documents.length}
        />
      </aside>

      {/* MAIN */}
      <section className="min-w-0 space-y-6">
        {/* Mode tabs */}
        <div className="bg-white border border-gray-100 rounded-2xl p-2 flex flex-wrap gap-1" data-testid="second-brain-modes">
          {MODES.map((m) => {
            const active = mode === m.id;
            return (
              <button
                key={m.id}
                onClick={() => setMode(m.id)}
                data-testid={`sb-mode-${m.id}`}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold transition-colors ${
                  active ? "bg-black text-white" : "text-gray-600 hover:bg-[#F6F7FB]"
                }`}
              >
                <m.Icon size={15} />
                {m.label}
              </button>
            );
          })}
          <div className="flex-1" />
          <div className="flex items-center gap-2 px-3 text-xs uppercase tracking-widest text-gray-500 font-bold">
            <Sparkles size={13} className="text-[#00A651]" />
            Claude Sonnet 4.6
          </div>
        </div>

        {loading && !overview ? (
          <div className="bg-white border border-gray-100 rounded-2xl p-16 grid place-items-center" data-testid="sb-loading">
            <Loader2 className="w-6 h-6 animate-spin text-[#00A651]" />
          </div>
        ) : (
          <AnimatePresence mode="wait">
            {mode === "navigator" && (
              <NavigatorView key="nav" overview={overview} recentDocs={scopedDocs.slice(0, 10)}
                onOpenPillar={setActivePillar} onCloseAction={closeAction} onRemoveDoc={removeDoc} onPreview={openPreview} />
            )}
            {mode === "detective" && (
              <DetectiveView key="det" api={api} activePillar={activePillar} pillars={pillars} />
            )}
            {mode === "drafter" && (
              <DrafterView key="drf" api={api} activePillar={activePillar} pillars={pillars} />
            )}
            {mode === "synthesizer" && (
              <SynthesizerView key="syn" api={api} activePillar={activePillar} pillars={pillars}
                onDone={loadAll} />
            )}
          </AnimatePresence>
        )}

        {/* Document library (always visible under the mode) */}
        {mode !== "synthesizer" && (
          <DocumentLibrary
            documents={scopedDocs}
            pillars={pillars}
            activePillar={activePillar}
            onSelectPillar={setActivePillar}
            onRemove={removeDoc}
            onPreview={openPreview}
          />
        )}
      </section>

      {/* Document preview modal */}
      {preview && (
        <DocPreviewModal preview={preview} pillars={pillars} onClose={() => setPreview(null)} />
      )}
    </div>
  );
}

/* ============================================================
   PILLAR RAIL + Upload dropzones
   ============================================================ */
function PillarRail({ pillars, overview, activePillar, onSelect, onUpload, totalDocs }) {
  const items = overview?.pillars || pillars.map((p) => ({ ...p, document_count: 0, open_actions: 0 }));
  return (
    <div className="bg-white border border-gray-100 rounded-2xl overflow-hidden">
      <div className="p-4 border-b border-gray-100 flex items-center justify-between">
        <p className="text-xs uppercase tracking-widest font-bold text-gray-500">Vault Pillars</p>
        <span className="text-xs font-bold text-black">{totalDocs} docs</span>
      </div>
      <button
        onClick={() => onSelect("all")}
        data-testid="sb-pillar-all"
        className={`w-full flex items-center justify-between px-4 py-3 text-sm font-bold text-left transition-colors ${
          activePillar === "all" ? "bg-[#F6F7FB] text-black" : "text-gray-600 hover:bg-[#F6F7FB]"
        }`}
      >
        <span>All Pillars</span>
        <span className="text-xs text-gray-500">{totalDocs}</span>
      </button>
      {items.map((p) => (
        <PillarRow key={p.id} p={p} active={activePillar === p.id} onSelect={() => onSelect(p.id)} onUpload={onUpload} />
      ))}
    </div>
  );
}

function PillarRow({ p, active, onSelect, onUpload }) {
  const [expanded, setExpanded] = useState(active);
  const inputRef = useRef(null);
  const [tags, setTags] = useState("");
  const [uploading, setUploading] = useState(false);

  useEffect(() => { if (active) setExpanded(true); }, [active]);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      await onUpload(p.id, file, tags);
      setTags("");
    } finally { setUploading(false); e.target.value = ""; }
  };

  const drop = async (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      await onUpload(p.id, file, tags);
      setTags("");
    } finally { setUploading(false); }
  };

  return (
    <div className={`border-t border-gray-100 ${active ? "bg-[#F6F7FB]" : ""}`}>
      <button
        onClick={() => { onSelect(); setExpanded((v) => !v); }}
        data-testid={`sb-pillar-${p.id}`}
        className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-[#F6F7FB]/60 transition-colors"
      >
        <span className="w-2 h-8 rounded-sm" style={{ backgroundColor: p.accent }} />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold text-black truncate">{p.name}</p>
          <p className="text-[10px] uppercase tracking-widest text-gray-500 mt-0.5">
            {p.document_count} docs · {p.open_actions} actions
          </p>
        </div>
        <ChevronRight size={14} className={`text-gray-400 transition-transform ${expanded ? "rotate-90" : ""}`} />
      </button>
      {expanded && (
        <div
          onDragOver={(e) => e.preventDefault()}
          onDrop={drop}
          data-testid={`sb-drop-${p.id}`}
          className="px-4 pb-4 space-y-2"
        >
          <p className="text-[11px] text-gray-500 leading-snug">{p.blurb}</p>
          <label className="block border-2 border-dashed border-gray-300 hover:border-[#00A651] hover:bg-white transition-colors rounded-xl p-3 cursor-pointer">
            <div className="flex items-center gap-2">
              {uploading ? <Loader2 className="w-4 h-4 animate-spin text-[#00A651]" /> : <UploadCloud size={14} className="text-[#00A651]" />}
              <span className="text-xs font-bold text-black">
                {uploading ? "Ingesting…" : "Drop PDF / DOCX / TXT"}
              </span>
            </div>
            <input
              ref={inputRef}
              type="file"
              accept=".pdf,.docx,.txt,.md"
              onChange={onFile}
              className="hidden"
              data-testid={`sb-upload-${p.id}`}
            />
          </label>
          <input
            value={tags}
            onChange={(e) => setTags(e.target.value)}
            placeholder="Tags (comma-separated)"
            data-testid={`sb-tags-${p.id}`}
            className="w-full text-xs px-3 py-2 rounded-lg bg-white border border-gray-200 focus:border-[#00A651] outline-none"
          />
        </div>
      )}
    </div>
  );
}

/* ============================================================
   AGENT 4 · NAVIGATOR
   ============================================================ */
function NavigatorView({ overview, recentDocs, onOpenPillar, onCloseAction, onRemoveDoc, onPreview }) {
  const pillars = overview?.pillars || [];
  const actions = overview?.open_actions || [];
  const totalDocs = pillars.reduce((s, p) => s + (p.document_count || 0), 0);
  const totalActions = actions.length;

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
      className="space-y-6" data-testid="sb-view-navigator">
      {/* Summary bar */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <SummaryTile label="Total Documents" value={totalDocs} accent="#00A651" />
        <SummaryTile label="Open Actions" value={totalActions} accent="#ED1C24" />
        <SummaryTile label="Pillars Active" value={pillars.filter((p) => p.document_count > 0).length} accent="#E88C2F" />
        <SummaryTile label="LLM" value="Sonnet 4.6" accent="#111111" small />
      </div>

      {/* Pillar tiles */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {pillars.map((p) => (
          <button
            key={p.id}
            onClick={() => onOpenPillar(p.id)}
            className="text-left bg-white border border-gray-100 rounded-2xl p-5 hover:shadow-md transition-shadow"
            data-testid={`sb-nav-tile-${p.id}`}
          >
            <div className="flex items-center gap-3">
              <span className="w-9 h-9 rounded-lg" style={{ backgroundColor: `${p.accent}20`, color: p.accent, display: "grid", placeItems: "center" }}>
                <FileText size={16} />
              </span>
              <p className="text-xs uppercase tracking-widest font-bold text-gray-500">{p.code}</p>
            </div>
            <p className="font-heading font-bold text-lg text-black mt-3 leading-tight">{p.name}</p>
            <p className="text-xs text-gray-500 mt-1 line-clamp-2">{p.blurb}</p>
            <div className="mt-4 flex items-center gap-4 text-xs">
              <span className="font-bold text-black">{p.document_count} docs</span>
              <span className="text-gray-500">{p.open_actions} open actions</span>
              <ChevronRight size={14} className="ml-auto text-gray-400" />
            </div>
          </button>
        ))}
      </div>

      {/* Open action items */}
      <div className="bg-white border border-gray-100 rounded-2xl overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
          <p className="text-xs uppercase tracking-widest font-bold text-gray-500">Open Action Items</p>
          <span className="text-xs font-bold text-black">{actions.length}</span>
        </div>
        {actions.length === 0 ? (
          <div className="p-8 text-center text-sm text-gray-500">
            No open action items. Synthesize a meeting note to auto-extract next steps.
          </div>
        ) : (
          <ul>
            {actions.map((a) => (
              <li key={a.id} className="px-5 py-3 border-b border-gray-50 last:border-b-0 flex items-start gap-3">
                <button
                  onClick={() => onCloseAction(a.id)}
                  data-testid={`sb-close-action-${a.id}`}
                  className="mt-1 w-5 h-5 rounded-md border-2 border-gray-300 hover:border-[#00A651] hover:bg-[#00A651]/10 grid place-items-center transition-colors"
                  title="Mark done"
                >
                  <CheckCircle2 size={12} className="text-[#00A651] opacity-0 hover:opacity-100" />
                </button>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-black">{a.title}</p>
                  {a.description && <p className="text-xs text-gray-500 mt-0.5 leading-snug">{a.description}</p>}
                  <div className="mt-1 flex flex-wrap items-center gap-2 text-[11px] text-gray-500">
                    <span className="uppercase tracking-widest font-bold">{a.pillar}</span>
                    {a.owner && <span>· {a.owner}</span>}
                    {a.due && <span>· due {a.due}</span>}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Recent uploads */}
      <div className="bg-white border border-gray-100 rounded-2xl overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100">
          <p className="text-xs uppercase tracking-widest font-bold text-gray-500">Recent Uploads</p>
        </div>
        {recentDocs.length === 0 ? (
          <div className="p-8 text-center text-sm text-gray-500">
            Nothing here yet. Drop a document into any pillar to get started.
          </div>
        ) : (
          <ul>
            {recentDocs.map((d) => (
              <li key={d.id} className="px-5 py-3 border-b border-gray-50 last:border-b-0 flex items-start gap-3 group hover:bg-[#F6F7FB]/60 transition-colors">
                <button
                  onClick={() => onPreview(d.id)}
                  className="flex items-start gap-3 flex-1 min-w-0 text-left"
                  data-testid={`sb-nav-preview-${d.id}`}
                >
                  <span className="w-8 h-8 rounded-lg bg-[#F6F7FB] group-hover:bg-white text-black grid place-items-center"><FileText size={14} /></span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-black truncate group-hover:text-[#00A651]">{d.filename}</p>
                    <p className="text-xs text-gray-500 line-clamp-2 mt-0.5">{d.summary || "No summary yet."}</p>
                  </div>
                </button>
                <button onClick={() => onPreview(d.id)} className="text-gray-400 hover:text-[#00A651] opacity-0 group-hover:opacity-100 transition-opacity" title="Preview">
                  <Eye size={14} />
                </button>
                <button onClick={() => onRemoveDoc(d.id)} className="text-gray-400 hover:text-[#ED1C24]" data-testid={`sb-remove-${d.id}`}>
                  <Trash2 size={14} />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </motion.div>
  );
}

function SummaryTile({ label, value, accent, small }) {
  return (
    <div className="bg-white border border-gray-100 rounded-2xl p-4">
      <p className="text-[10px] uppercase tracking-widest font-bold text-gray-500">{label}</p>
      <p className={`font-heading font-bold text-black mt-2 tracking-tight ${small ? "text-lg" : "text-3xl"}`}>{value}</p>
      <div className="mt-2 h-1 w-8 rounded-full" style={{ backgroundColor: accent }} />
    </div>
  );
}

/* ============================================================
   AGENT 1 · DOCUMENT DETECTIVE
   ============================================================ */
function DetectiveView({ api, activePillar, pillars }) {
  const [query, setQuery] = useState("");
  const [history, setHistory] = useState([]); // { role, text, citations? }
  const [asking, setAsking] = useState(false);
  const scrollRef = useRef(null);
  useEffect(() => { scrollRef.current?.scrollTo({ top: 99999, behavior: "smooth" }); }, [history]);

  const ask = async (e) => {
    e?.preventDefault();
    if (!query.trim() || asking) return;
    const q = query.trim();
    setQuery("");
    setHistory((h) => [...h, { role: "user", text: q }]);
    setAsking(true);
    try {
      const r = await api.post("/admin/second-brain/ask", {
        query: q,
        pillar: activePillar === "all" ? null : activePillar,
        top_k: 5,
      });
      setHistory((h) => [...h, { role: "agent", text: r.data.answer, citations: r.data.citations || [] }]);
    } catch (err) {
      setHistory((h) => [...h, { role: "agent", text: err?.response?.data?.detail || "Query failed.", citations: [] }]);
    } finally { setAsking(false); }
  };

  const pillarLabel = activePillar === "all" ? "All Pillars" : pillars.find((p) => p.id === activePillar)?.name || activePillar;

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
      className="bg-white border border-gray-100 rounded-2xl overflow-hidden" data-testid="sb-view-detective">
      <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
        <div>
          <p className="text-xs uppercase tracking-widest font-bold text-[#00A651]">Agent 1 · Document Detective</p>
          <p className="font-heading font-bold text-lg text-black mt-1">Ask the vault</p>
        </div>
        <span className="text-[11px] uppercase tracking-widest font-bold text-gray-500">Scope · {pillarLabel}</span>
      </div>

      <div ref={scrollRef} className="max-h-[520px] overflow-y-auto p-5 space-y-4" data-testid="sb-detective-log">
        {history.length === 0 && (
          <div className="text-center py-12">
            <div className="w-12 h-12 grid place-items-center rounded-xl bg-[#00A651]/10 text-[#00A651] mx-auto"><Search size={18} /></div>
            <p className="mt-4 text-sm font-bold text-black">Try asking about any uploaded document.</p>
            <p className="mt-1 text-xs text-gray-500 max-w-md mx-auto">
              e.g. &ldquo;Where is the credit intake sheet for our retention program?&rdquo; · &ldquo;Summarize the lending requirements from the SOP.&rdquo;
            </p>
          </div>
        )}
        {history.map((m, i) => (
          <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-2xl rounded-2xl px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap ${
              m.role === "user" ? "bg-black text-white" : "bg-[#F6F7FB] text-black"
            }`}>
              {m.text}
              {m.citations && m.citations.length > 0 && (
                <div className="mt-3 space-y-1.5 border-t border-black/10 pt-3">
                  <p className="text-[10px] uppercase tracking-widest font-bold text-gray-500">Citations</p>
                  {m.citations.map((c, j) => (
                    <div key={j} className="text-xs bg-white border border-gray-200 rounded-lg px-3 py-2">
                      <p className="font-bold text-black">[{j + 1}] {c.filename} · chunk {c.chunk_index}</p>
                      <p className="text-gray-500 mt-1 line-clamp-2">{c.snippet}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}
        {asking && (
          <div className="flex justify-start">
            <div className="bg-[#F6F7FB] rounded-2xl px-4 py-3 text-sm flex items-center gap-2 text-gray-500">
              <Loader2 className="w-4 h-4 animate-spin" /> Searching the vault…
            </div>
          </div>
        )}
      </div>

      <form onSubmit={ask} className="border-t border-gray-100 p-4 flex items-center gap-2">
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Ask Ms. Diane's Second Brain…"
          data-testid="sb-detective-input"
          className="flex-1 px-4 py-3 rounded-xl bg-[#F6F7FB] border border-transparent focus:border-[#00A651] focus:bg-white outline-none text-sm"
        />
        <button
          type="submit"
          disabled={asking || !query.trim()}
          data-testid="sb-detective-submit"
          className="h-11 px-5 rounded-xl bg-[#00A651] hover:bg-[#008f45] disabled:opacity-50 text-white font-bold uppercase tracking-wider text-xs flex items-center gap-2 transition-colors"
        >
          <Send size={14} /> Ask
        </button>
      </form>
    </motion.div>
  );
}

/* ============================================================
   AGENT 2 · PROPOSAL DRAFTER
   ============================================================ */
const TEMPLATES = [
  { id: "credit_plan",  label: "3-Step Credit Readiness Plan",  hint: "Client / context brief" },
  { id: "video_script", label: "Short-form Video Script",       hint: "Topic / angle for the video" },
  { id: "exec_overview", label: "Executive Overview (Partners)", hint: "Raw notes / context" },
  { id: "freeform",     label: "Freeform Draft",                hint: "Describe what you need" },
];

function DrafterView({ api, activePillar, pillars }) {
  const [template, setTemplate] = useState("credit_plan");
  const [brief, setBrief] = useState("");
  const [output, setOutput] = useState("");
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  const submit = async (e) => {
    e?.preventDefault();
    if (!brief.trim() || busy) return;
    setBusy(true); setOutput("");
    try {
      const r = await api.post("/admin/second-brain/draft", {
        template,
        brief: brief.trim(),
        pillar: activePillar === "all" ? null : activePillar,
      });
      setOutput(r.data.output || "");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Draft failed.");
    } finally { setBusy(false); }
  };

  const copyOut = async () => {
    try { await navigator.clipboard.writeText(output); setCopied(true); setTimeout(() => setCopied(false), 1500); }
    catch { toast.error("Copy failed."); }
  };

  const currentTemplate = TEMPLATES.find((t) => t.id === template);
  const pillarLabel = activePillar === "all" ? "All Pillars" : pillars.find((p) => p.id === activePillar)?.name || activePillar;

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
      className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="sb-view-drafter">
      {/* Input */}
      <form onSubmit={submit} className="bg-white border border-gray-100 rounded-2xl overflow-hidden flex flex-col">
        <div className="px-5 py-4 border-b border-gray-100">
          <p className="text-xs uppercase tracking-widest font-bold text-[#E88C2F]">Agent 2 · Proposal Drafter</p>
          <p className="font-heading font-bold text-lg text-black mt-1">Draft in her voice</p>
          <span className="text-[11px] uppercase tracking-widest font-bold text-gray-500 mt-1 block">Pillar · {pillarLabel}</span>
        </div>
        <div className="p-5 space-y-4 flex-1 flex flex-col">
          <div>
            <p className="text-[10px] uppercase tracking-widest font-bold text-gray-500 mb-2">Template</p>
            <div className="grid grid-cols-2 gap-2">
              {TEMPLATES.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setTemplate(t.id)}
                  data-testid={`sb-draft-tpl-${t.id}`}
                  className={`text-left text-xs font-bold px-3 py-2.5 rounded-xl border transition-colors ${
                    template === t.id
                      ? "border-[#00A651] bg-[#00A651]/10 text-black"
                      : "border-gray-200 text-gray-600 hover:border-gray-300"
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>
          <div className="flex-1 flex flex-col">
            <p className="text-[10px] uppercase tracking-widest font-bold text-gray-500">{currentTemplate?.hint || "Brief"}</p>
            <textarea
              value={brief}
              onChange={(e) => setBrief(e.target.value)}
              placeholder="Type or paste context. The drafter uses this as the brief for Claude."
              data-testid="sb-draft-brief"
              rows={10}
              className="mt-2 flex-1 w-full px-3 py-2 rounded-lg bg-[#F6F7FB] border border-transparent focus:border-[#00A651] focus:bg-white outline-none text-sm resize-none"
            />
          </div>
          <button
            type="submit"
            disabled={busy || !brief.trim()}
            data-testid="sb-draft-submit"
            className="w-full h-11 rounded-xl bg-black hover:bg-[#111] disabled:opacity-50 text-white font-bold uppercase tracking-wider text-xs flex items-center justify-center gap-2 transition-colors"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles size={14} />}
            {busy ? "Drafting…" : "Generate Draft"}
          </button>
        </div>
      </form>

      {/* Output */}
      <div className="bg-white border border-gray-100 rounded-2xl overflow-hidden flex flex-col">
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
          <p className="text-xs uppercase tracking-widest font-bold text-gray-500">Draft Output</p>
          {output && (
            <button onClick={copyOut} data-testid="sb-draft-copy" className="text-xs font-bold text-gray-600 hover:text-black flex items-center gap-1">
              {copied ? <CheckCircle2 size={13} className="text-[#00A651]" /> : <Copy size={13} />}
              {copied ? "Copied" : "Copy"}
            </button>
          )}
        </div>
        <div className="p-5 flex-1 overflow-y-auto max-h-[560px]">
          {!output && !busy && (
            <div className="text-center text-sm text-gray-500 py-12">
              Choose a template, add your brief, then generate.
            </div>
          )}
          {busy && (
            <div className="flex items-center justify-center py-12 gap-2 text-sm text-gray-500">
              <Loader2 className="w-4 h-4 animate-spin" /> Drafting with Claude Sonnet 4.6…
            </div>
          )}
          {output && (
            <pre data-testid="sb-draft-output" className="whitespace-pre-wrap text-sm text-black font-sans leading-relaxed">
              {output}
            </pre>
          )}
        </div>
      </div>
    </motion.div>
  );
}

/* ============================================================
   AGENT 3 · MEETING / NOTE SYNTHESIZER
   ============================================================ */
function SynthesizerView({ api, activePillar, pillars, onDone }) {
  const [text, setText] = useState("");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e?.preventDefault();
    if (text.trim().length < 20 || busy) return;
    setBusy(true); setResult(null);
    try {
      const r = await api.post("/admin/second-brain/synthesize", {
        text: text.trim(),
        pillar: activePillar === "all" ? null : activePillar,
      });
      setResult(r.data);
      toast.success("Synthesized. Action items filed to Navigator.");
      onDone && onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Synthesis failed.");
    } finally { setBusy(false); }
  };

  const pillarLabel = activePillar === "all" ? "auto-infer" : pillars.find((p) => p.id === activePillar)?.name || activePillar;

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
      className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="sb-view-synthesizer">
      <form onSubmit={submit} className="bg-white border border-gray-100 rounded-2xl overflow-hidden flex flex-col">
        <div className="px-5 py-4 border-b border-gray-100">
          <p className="text-xs uppercase tracking-widest font-bold text-[#ED1C24]">Agent 3 · Meeting Synthesizer</p>
          <p className="font-heading font-bold text-lg text-black mt-1">Turn notes into next steps</p>
          <span className="text-[11px] uppercase tracking-widest font-bold text-gray-500 mt-1 block">Pillar · {pillarLabel}</span>
        </div>
        <div className="p-5 flex-1 flex flex-col gap-4">
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Paste raw meeting notes, a voice transcript, or an email thread. Minimum 20 characters."
            data-testid="sb-synth-input"
            rows={14}
            className="flex-1 w-full px-3 py-2 rounded-lg bg-[#F6F7FB] border border-transparent focus:border-[#00A651] focus:bg-white outline-none text-sm resize-none"
          />
          <button
            type="submit"
            disabled={busy || text.trim().length < 20}
            data-testid="sb-synth-submit"
            className="w-full h-11 rounded-xl bg-[#ED1C24] hover:bg-[#c9161e] disabled:opacity-50 text-white font-bold uppercase tracking-wider text-xs flex items-center justify-center gap-2 transition-colors"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ClipboardList size={14} />}
            {busy ? "Synthesizing…" : "Synthesize"}
          </button>
        </div>
      </form>

      <div className="bg-white border border-gray-100 rounded-2xl overflow-hidden flex flex-col">
        <div className="px-5 py-4 border-b border-gray-100">
          <p className="text-xs uppercase tracking-widest font-bold text-gray-500">Executive Snapshot</p>
        </div>
        <div className="p-5 flex-1 overflow-y-auto max-h-[560px] space-y-5">
          {!result && !busy && (
            <div className="text-center text-sm text-gray-500 py-12">
              Paste notes on the left. Ms. Diane gets: 3-bullet summary · key decisions · action items filed by pillar.
            </div>
          )}
          {busy && (
            <div className="flex items-center justify-center py-12 gap-2 text-sm text-gray-500">
              <Loader2 className="w-4 h-4 animate-spin" /> Reading between the lines…
            </div>
          )}
          {result && (
            <>
              <section>
                <p className="text-[10px] uppercase tracking-widest font-bold text-gray-500">Executive Summary</p>
                <p className="mt-2 text-sm text-black whitespace-pre-wrap" data-testid="sb-synth-summary">{result.summary || "—"}</p>
              </section>
              <section>
                <p className="text-[10px] uppercase tracking-widest font-bold text-gray-500">Key Decisions</p>
                {(result.decisions || []).length === 0 ? (
                  <p className="mt-2 text-sm text-gray-500">None extracted.</p>
                ) : (
                  <ul className="mt-2 space-y-1.5">
                    {result.decisions.map((d, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm">
                        <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[#E88C2F]" />
                        <span className="text-black">{d}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
              <section>
                <p className="text-[10px] uppercase tracking-widest font-bold text-gray-500">Action Items</p>
                {(result.actions || []).length === 0 ? (
                  <p className="mt-2 text-sm text-gray-500">None extracted.</p>
                ) : (
                  <ul className="mt-2 space-y-2" data-testid="sb-synth-actions">
                    {result.actions.map((a) => (
                      <li key={a.id} className="border border-gray-100 rounded-xl px-3 py-2">
                        <p className="text-sm font-bold text-black">{a.title}</p>
                        {a.description && <p className="text-xs text-gray-500 mt-0.5">{a.description}</p>}
                        <div className="mt-1 flex gap-3 text-[11px] text-gray-500">
                          {a.owner && <span>Owner: <span className="font-bold text-black">{a.owner}</span></span>}
                          {a.due && <span>Due: <span className="font-bold text-black">{a.due}</span></span>}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
              {result.pillar_suggestion && (
                <p className="text-[11px] uppercase tracking-widest font-bold text-gray-500">
                  Filed under · <span className="text-[#00A651]">{result.pillar_suggestion}</span>
                </p>
              )}
            </>
          )}
        </div>
      </div>
    </motion.div>
  );
}

/* ============================================================
   DOCUMENT LIBRARY
   ============================================================ */
function DocumentLibrary({ documents, pillars, activePillar, onSelectPillar, onRemove, onPreview }) {
  const pillarName = activePillar === "all" ? "All Pillars" : pillars.find((p) => p.id === activePillar)?.name;
  return (
    <div className="bg-white border border-gray-100 rounded-2xl overflow-hidden" data-testid="sb-library">
      <div className="px-5 py-4 border-b border-gray-100 flex flex-wrap items-center gap-3">
        <div>
          <p className="text-xs uppercase tracking-widest font-bold text-gray-500">Document Library</p>
          <p className="font-heading font-bold text-lg text-black mt-1">{pillarName}</p>
        </div>
        <span className="text-xs font-bold text-black ml-auto">{documents.length} docs</span>
        {activePillar !== "all" && (
          <button onClick={() => onSelectPillar("all")} className="text-xs font-bold text-gray-500 hover:text-black flex items-center gap-1" data-testid="sb-library-clear">
            <X size={12} /> Clear filter
          </button>
        )}
      </div>
      {documents.length === 0 ? (
        <div className="p-8 text-center text-sm text-gray-500">
          {activePillar === "all"
            ? "Vault is empty. Drop a document into any pillar on the left."
            : "This pillar has no documents yet. Drop one on the left."}
        </div>
      ) : (
        <ul>
          {documents.map((d) => (
            <li key={d.id} className="px-5 py-3 border-b border-gray-50 last:border-b-0 flex items-start gap-3 group hover:bg-[#F6F7FB]/60 transition-colors">
              <button
                onClick={() => onPreview(d.id)}
                className="flex items-start gap-3 flex-1 min-w-0 text-left"
                data-testid={`sb-lib-preview-${d.id}`}
              >
                <span className="w-8 h-8 rounded-lg bg-[#F6F7FB] group-hover:bg-white text-black grid place-items-center"><FileText size={14} /></span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-black truncate group-hover:text-[#00A651]">{d.filename}</p>
                  <p className="text-xs text-gray-500 line-clamp-2 mt-0.5">{d.summary || "No summary."}</p>
                  {(d.tags || []).length > 0 && (
                    <div className="mt-1.5 flex flex-wrap gap-1">
                      {d.tags.slice(0, 6).map((t) => (
                        <span key={t} className="text-[10px] uppercase tracking-widest font-bold bg-[#F6F7FB] group-hover:bg-white text-gray-600 px-2 py-0.5 rounded">
                          {t}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </button>
              <div className="text-right shrink-0 flex flex-col items-end gap-1">
                <p className="text-[10px] uppercase tracking-widest font-bold text-gray-500">
                  {new Date(d.uploaded_at).toLocaleDateString()}
                </p>
                <p className="text-[10px] text-gray-500">{d.chunk_count} chunks</p>
                <div className="flex items-center gap-2 mt-1">
                  <button onClick={() => onPreview(d.id)} className="text-gray-400 hover:text-[#00A651]" title="Preview" data-testid={`sb-lib-eye-${d.id}`}>
                    <Eye size={13} />
                  </button>
                  <button onClick={() => onRemove(d.id)} className="text-gray-400 hover:text-[#ED1C24]" data-testid={`sb-lib-remove-${d.id}`}>
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* ============================================================
   DOC PREVIEW MODAL — side-by-side full text + summary
   ============================================================ */
function DocPreviewModal({ preview, pillars, onClose }) {
  const { loading, doc, error } = preview;
  const [copied, setCopied] = useState(false);

  // Close on Esc
  useEffect(() => {
    const h = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);

  const copyText = async () => {
    if (!doc?.full_text) return;
    try {
      await navigator.clipboard.writeText(doc.full_text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { toast.error("Copy failed."); }
  };

  const pillar = pillars.find((p) => p.id === doc?.pillar);

  return (
    <div className="fixed inset-0 z-50 grid place-items-center p-4 sm:p-8" data-testid="sb-doc-preview">
      {/* backdrop */}
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
        data-testid="sb-doc-preview-backdrop"
      />
      {/* modal */}
      <motion.div
        initial={{ opacity: 0, y: 12, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.2 }}
        className="relative w-full max-w-6xl h-[85vh] bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col"
      >
        {/* Accent stripe */}
        <div className="h-1 flex shrink-0">
          <span className="flex-1" style={{ backgroundColor: pillar?.accent || "#00A651" }} />
          <span className="flex-1 bg-[#E88C2F]" />
          <span className="flex-1 bg-[#ED1C24]" />
          <span className="flex-1 bg-[#F26522]" />
        </div>

        {/* Header */}
        <header className="px-6 py-4 border-b border-gray-100 flex items-start gap-4 shrink-0">
          <span className="w-11 h-11 rounded-xl grid place-items-center shrink-0"
                style={{ backgroundColor: `${pillar?.accent || "#00A651"}15`, color: pillar?.accent || "#00A651" }}>
            <FileText size={18} />
          </span>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] uppercase tracking-[0.3em] font-bold text-gray-500">
              {pillar?.code || "Document"}
            </p>
            <h2 className="font-heading font-bold text-xl text-black mt-1 truncate" data-testid="sb-preview-title">
              {loading ? "Loading…" : (doc?.filename || "Document")}
            </h2>
            {doc && (
              <div className="mt-2 flex flex-wrap items-center gap-3 text-[11px] text-gray-500">
                <span className="uppercase tracking-widest font-bold" style={{ color: pillar?.accent }}>
                  {pillar?.name || doc.pillar}
                </span>
                <span>·</span>
                <span>{doc.chunk_count} chunks</span>
                <span>·</span>
                <span>{doc.char_count.toLocaleString()} chars</span>
                <span>·</span>
                <span>{new Date(doc.uploaded_at).toLocaleString()}</span>
              </div>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {doc && (
              <button
                onClick={copyText}
                data-testid="sb-preview-copy"
                className="text-xs font-bold text-gray-600 hover:text-black flex items-center gap-1 px-3 h-9 rounded-lg border border-gray-200 hover:border-gray-300"
              >
                {copied ? <CheckCircle2 size={13} className="text-[#00A651]" /> : <Copy size={13} />}
                {copied ? "Copied" : "Copy Text"}
              </button>
            )}
            <button
              onClick={onClose}
              data-testid="sb-preview-close"
              className="w-9 h-9 grid place-items-center rounded-lg text-gray-400 hover:bg-[#F6F7FB] hover:text-black"
              title="Close (Esc)"
            >
              <X size={16} />
            </button>
          </div>
        </header>

        {/* Body */}
        {loading && (
          <div className="flex-1 grid place-items-center">
            <Loader2 className="w-6 h-6 animate-spin text-[#00A651]" />
          </div>
        )}
        {error && !loading && (
          <div className="flex-1 grid place-items-center p-8">
            <div className="text-center max-w-sm">
              <div className="w-12 h-12 rounded-xl bg-[#ED1C24]/10 text-[#ED1C24] grid place-items-center mx-auto">
                <X size={18} />
              </div>
              <p className="mt-4 font-heading font-bold text-lg text-black">Couldn&apos;t load</p>
              <p className="mt-1 text-sm text-gray-500">{error}</p>
            </div>
          </div>
        )}
        {doc && !loading && (
          <div className="flex-1 grid grid-cols-1 lg:grid-cols-[380px_minmax(0,1fr)] min-h-0 overflow-hidden">
            {/* AI summary sidebar */}
            <aside className="border-r border-gray-100 bg-[#FAFAFB] overflow-y-auto">
              <div className="p-6 space-y-5">
                <section>
                  <div className="flex items-center gap-2">
                    <Sparkles size={13} className="text-[#00A651]" />
                    <p className="text-[10px] uppercase tracking-[0.3em] font-bold text-[#00A651]">AI Summary</p>
                  </div>
                  <p className="mt-3 text-sm text-black leading-relaxed whitespace-pre-wrap" data-testid="sb-preview-summary">
                    {doc.summary || "No AI summary was generated for this document."}
                  </p>
                </section>

                {(doc.tags || []).length > 0 && (
                  <section>
                    <p className="text-[10px] uppercase tracking-[0.3em] font-bold text-gray-500">Tags</p>
                    <div className="mt-2 flex flex-wrap gap-1.5" data-testid="sb-preview-tags">
                      {doc.tags.map((t) => (
                        <span key={t} className="text-[10px] uppercase tracking-widest font-bold bg-white border border-gray-200 text-gray-700 px-2 py-1 rounded">
                          {t}
                        </span>
                      ))}
                    </div>
                  </section>
                )}

                <section>
                  <p className="text-[10px] uppercase tracking-[0.3em] font-bold text-gray-500">Metadata</p>
                  <dl className="mt-2 space-y-1.5 text-xs">
                    <Row k="Filename" v={doc.filename} />
                    <Row k="Type" v={doc.mime || "—"} />
                    <Row k="Size" v={`${(doc.size / 1024).toFixed(1)} KB`} />
                    <Row k="Chunks" v={doc.chunk_count} />
                    <Row k="Characters" v={doc.char_count.toLocaleString()} />
                    <Row k="Pillar" v={pillar?.name || doc.pillar} />
                    <Row k="Uploaded" v={new Date(doc.uploaded_at).toLocaleString()} />
                  </dl>
                </section>
              </div>
            </aside>

            {/* Full extracted text */}
            <div className="overflow-y-auto bg-white">
              <div className="p-6">
                <div className="flex items-center gap-2 mb-3">
                  <FileText size={13} className="text-black" />
                  <p className="text-[10px] uppercase tracking-[0.3em] font-bold text-black">Extracted Text</p>
                  <span className="text-[10px] text-gray-400">· {doc.char_count.toLocaleString()} chars</span>
                </div>
                {doc.full_text ? (
                  <pre
                    data-testid="sb-preview-fulltext"
                    className="whitespace-pre-wrap font-mono text-[12.5px] leading-relaxed text-gray-800 bg-[#FAFAFB] border border-gray-100 rounded-xl p-4"
                  >
                    {doc.full_text}
                  </pre>
                ) : (
                  <p className="text-sm text-gray-500 italic">No text was extracted from this document.</p>
                )}
              </div>
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex items-baseline gap-3">
      <dt className="text-gray-500 text-[10px] uppercase tracking-widest font-bold w-24 shrink-0">{k}</dt>
      <dd className="text-black text-xs break-words">{v}</dd>
    </div>
  );
}

